import { useTranslation } from 'next-i18next';
import useUser from '@hooks/user/use-user';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { PageType } from '@interfaces/common/common';
import { FAV_DEFAULT_SORT_ORDER } from '@constants/profile-fav-sort';
import { useRouter } from 'next/router';
import { ProductGeneralComponentProps } from '@interfaces/models/cms/product-general';

interface FavoritesPageDataProps extends ProductGeneralComponentProps {
  header: {
    title: string;
    description: string;
    cta: {
      text: string;
      url: string;
    };
  };
}
interface FavoritesEmptyStateProps {
  isHotFiltersEnabledAndInUrl?: boolean;
  favoritePageData?: FavoritesPageDataProps;
  eventCategory: string;
  pageType: PageType;
  shouldDisplayCMSBlockInEmptyState?: boolean;
}

const getFallbackTexts = (isMyFavorites) => {
  const title = isMyFavorites ? 'FAVORITE_PAGE.EMPTY.TITLE' : 'FAVORITES.EMPTY.TITLE.TEXT';

  const subtitle = isMyFavorites ? 'FAVORITE_PAGE.EMPTY.SUBTITLE' : 'FAVORITES.EMPTY.SUB_TITLE.TEXT';

  const cta = isMyFavorites ? 'FAVORITE_PAGE.EMPTY.CTA' : '';

  return {
    title,
    subtitle,
    cta,
  };
};

export const useFavoritesEmptyState = ({
  isHotFiltersEnabledAndInUrl = false,
  favoritePageData,
  eventCategory,
  pageType,
  shouldDisplayCMSBlockInEmptyState = false,
}: FavoritesEmptyStateProps) => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useUser();
  const { sendAnalyticEvent } = useAnalyticEvents('favorites');
  const router = useRouter();
  const { sortBy, favoritesUserSlug, profileSlug } = router?.query as {
    sortBy?: string;
    favoritesUserSlug?: string;
    profileSlug?: string;
  };

  const slug = favoritesUserSlug?.[0] || profileSlug?.[0];
  const isMyFavorites = slug === user?.id;

  const { title, subtitle, cta } = getFallbackTexts(isMyFavorites);

  const headerTitle = favoritePageData?.header?.title || t(title);
  const headerDescription = favoritePageData?.header?.description || t(subtitle);
  const headerCtaTitle = favoritePageData?.header?.cta?.text || t(cta);
  const headerCtaUrl = favoritePageData?.header?.cta?.url || '/new-items';

  const getFavoritesLink = (slug: string, pageType: PageType, sortBy: string): string => {
    const basePath = pageType === 'profile-favorites' ? `profile/${slug}` : `/favorites/${slug}`;
    return `${basePath}/?sortBy=${sortBy || FAV_DEFAULT_SORT_ORDER}${
      pageType === 'profile-favorites' ? '&tab=favorites' : ''
    }`;
  };

  const handleExploreMoreCta = (): void => {
    if (!isAuthenticated) {
      return;
    }

    sendAnalyticEvent('favourites_empty_state', {
      category: eventCategory,
      label: isHotFiltersEnabledAndInUrl ? 'try_new_filters' : 'find_fresh_listings',
    });

    const favoritesLink: string = getFavoritesLink(slug, pageType, sortBy);
    window.location.href = isHotFiltersEnabledAndInUrl ? favoritesLink : headerCtaUrl;
  };

  return {
    titleText: isHotFiltersEnabledAndInUrl ? t('FAVORITE_PAGE.FILTER_EMPTY.TITLE') : headerTitle,

    subTitleText: isHotFiltersEnabledAndInUrl ? t('FAVORITE_PAGE.FILTER_EMPTY.SUBTITLE') : headerDescription,

    ctaText: isHotFiltersEnabledAndInUrl ? t('FAVORITE_PAGE.FILTER_EMPTY.CTA') : headerCtaTitle,

    clickHandler: handleExploreMoreCta,
    pageType: pageType || 'favourite',
    image: {
      path: isHotFiltersEnabledAndInUrl
        ? '/images/empty-state/no-filters-results.svg'
        : '/images/profile-page/bag-heart.svg',
    },
    hasCMSProductsBlock: shouldDisplayCMSBlockInEmptyState,
  };
};
